import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useUser } from '../../contextapi/UserContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { Upload, Camera, User, Check, X, ZoomIn } from 'lucide-react';

// ── Constants ──────────────────────────────────────────────────────────────────
const VP_H = 300;

type OutputSize = 200 | 400 | 600;

// ── Types ──────────────────────────────────────────────────────────────────────
type CropState = {
  imgScale: number;   // current display scale of the image
  imgLeft:  number;   // image left edge in viewport px
  imgTop:   number;   // image top edge in viewport px
  fitScale: number;   // minimum scale (fills viewport)
  cropCX:   number;   // crop-circle center X in viewport px
  cropCY:   number;   // crop-circle center Y in viewport px
  cropR:    number;   // crop-circle radius in viewport px
  naturalW: number;   // natural image width px
  naturalH: number;   // natural image height px
};

type DragMode = 'none' | 'circle' | 'resize' | 'image';

const DEFAULT_CS: CropState = {
  imgScale: 1, imgLeft: 0, imgTop: 0, fitScale: 1,
  cropCX: 230, cropCY: 230, cropR: 150, naturalW: 0, naturalH: 0,
};

// ── Component ──────────────────────────────────────────────────────────────────
interface ProfilePictureUploaderProps {
  onUploadComplete?: (avatarUrl: string) => void;
  showTitle?: boolean;
}

export const ProfilePictureUploader: React.FC<ProfilePictureUploaderProps> = ({
  onUploadComplete,
  showTitle = true,
}) => {
  const { user } = useUser();
  const { toast } = useToast();

  // ── Upload state
  const [uploading,  setUploading]  = useState(false);
  const [avatarUrl,  setAvatarUrl]  = useState<string>('');
  const [dragOver,   setDragOver]   = useState(false);
  const [avatarDims, setAvatarDims] = useState<{ w: number; h: number } | null>(null);

  // ── Crop state
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingUrl,  setPendingUrl]  = useState<string>('');
  const outputSize: OutputSize = 400;
  const [cs,             setCs]             = useState<CropState>(DEFAULT_CS);
  const [vpCursor,       setVpCursor]       = useState<string>('grab');
  const [hintDismissed,  setHintDismissed]  = useState(false);

  // ── Refs
  const csRef      = useRef<CropState>(DEFAULT_CS);
  const dragRef    = useRef<{
    mode: DragMode; startX: number; startY: number;
    startCs: CropState; pinchDist: number;
  }>({ mode: 'none', startX: 0, startY: 0, startCs: DEFAULT_CS, pinchDist: 0 });
  const vpRef      = useRef<HTMLDivElement>(null);
  const cropImgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Keep ref in sync
  useEffect(() => { csRef.current = cs; }, [cs]);

  React.useEffect(() => { if (user?.id) loadCurrentAvatar(); }, [user?.id]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getVpW = () => vpRef.current?.getBoundingClientRect().width ?? 460;

  const applyCs = useCallback((next: CropState) => {
    csRef.current = next;
    setCs(next);
  }, []);

  /** Clamp crop circle: radius limited to viewport, center stays within viewport */
  const clampCrop = useCallback((s: CropState): CropState => {
    const vpW  = getVpW();
    const maxR = Math.min(vpW, VP_H) / 2 * 0.96;
    const r    = Math.max(20, Math.min(maxR, s.cropR));
    const cx   = Math.max(r, Math.min(vpW  - r, s.cropCX));
    const cy   = Math.max(r, Math.min(VP_H - r, s.cropCY));
    return { ...s, cropR: r, cropCX: cx, cropCY: cy };
  }, []);

  /** Keep image within viewport so it stays draggable and visible */
  const clampImg = useCallback((s: CropState): CropState => {
    const vpW  = getVpW();
    const iw   = s.naturalW * s.imgScale;
    const ih   = s.naturalH * s.imgScale;
    // Image can be positioned anywhere but at least 20% of it must remain in viewport
    const minVisible = 0.2;
    const imgLeft = Math.max(-(iw * (1 - minVisible)), Math.min(vpW  * (1 - minVisible), s.imgLeft));
    const imgTop  = Math.max(-(ih * (1 - minVisible)), Math.min(VP_H * (1 - minVisible), s.imgTop));
    return { ...s, imgLeft, imgTop };
  }, []);

  const detectMode = (mx: number, my: number, s: CropState): DragMode => {
    // SE handle at 45°
    const hx = s.cropCX + s.cropR * Math.SQRT1_2;
    const hy = s.cropCY + s.cropR * Math.SQRT1_2;
    if (Math.hypot(mx - hx, my - hy) <= 18) return 'resize';
    if (Math.hypot(mx - s.cropCX, my - s.cropCY) <= s.cropR) return 'circle';
    return 'image';
  };

  // ── Document-level mouse events ────────────────────────────────────────────
  useEffect(() => {
    if (!pendingFile) return;

    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (d.mode === 'none') return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      const ss  = d.startCs;
      let next: CropState;

      if (d.mode === 'circle') {
        next = clampCrop({ ...ss, cropCX: ss.cropCX + dx, cropCY: ss.cropCY + dy });
      } else if (d.mode === 'resize') {
        const el = vpRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const newR = Math.hypot(e.clientX - rect.left - ss.cropCX, e.clientY - rect.top - ss.cropCY);
        next = clampCrop({ ...ss, cropR: newR });
      } else {
        next = clampImg({ ...ss, imgLeft: ss.imgLeft + dx, imgTop: ss.imgTop + dy });
      }
      applyCs(next);
    };

    const onUp = () => {
      dragRef.current.mode = 'none';
      setVpCursor('grab');
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };
  }, [pendingFile, clampCrop, clampImg, applyCs]);

  // ── Scroll-wheel zoom ──────────────────────────────────────────────────────
  useEffect(() => {
    const el = vpRef.current;
    if (!el || !pendingFile) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const s = csRef.current;
      const rect = el.getBoundingClientRect();
      const mx   = e.clientX - rect.left;
      const my   = e.clientY - rect.top;
      const factor   = e.deltaY > 0 ? 0.92 : 1.09;
      const newScale = Math.max(s.fitScale, Math.min(s.fitScale * 8, s.imgScale * factor));
      const ratio    = newScale / s.imgScale;
      applyCs(clampImg({
        ...s,
        imgScale: newScale,
        imgLeft:  mx - (mx - s.imgLeft) * ratio,
        imgTop:   my - (my - s.imgTop)  * ratio,
      }));
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [pendingFile, clampImg, applyCs]);

  // ── React viewport handlers ────────────────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent) => {
    if (!vpRef.current) return;
    setHintDismissed(true);
    const rect  = vpRef.current.getBoundingClientRect();
    const mx    = e.clientX - rect.left;
    const my    = e.clientY - rect.top;
    const state = csRef.current;
    const mode  = detectMode(mx, my, state);
    dragRef.current = { mode, startX: e.clientX, startY: e.clientY, startCs: { ...state }, pinchDist: 0 };
    setVpCursor(mode === 'resize' ? 'nwse-resize' : 'grabbing');
    e.preventDefault();
  };

  const onMouseMoveVp = (e: React.MouseEvent) => {
    if (dragRef.current.mode !== 'none' || !vpRef.current) return;
    const rect = vpRef.current.getBoundingClientRect();
    const mx   = e.clientX - rect.left;
    const my   = e.clientY - rect.top;
    const mode = detectMode(mx, my, csRef.current);
    const next = mode === 'resize' ? 'nwse-resize' : mode === 'circle' ? 'move' : 'grab';
    setVpCursor(c => c !== next ? next : c);
  };

  // ── Touch ──────────────────────────────────────────────────────────────────
  const onTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    if (!vpRef.current) return;
    const rect = vpRef.current.getBoundingClientRect();
    if (e.touches.length === 1) {
      const mx    = e.touches[0].clientX - rect.left;
      const my    = e.touches[0].clientY - rect.top;
      const state = csRef.current;
      const mode  = detectMode(mx, my, state);
      dragRef.current = { mode, startX: e.touches[0].clientX, startY: e.touches[0].clientY, startCs: { ...state }, pinchDist: 0 };
    } else if (e.touches.length === 2) {
      const pd = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      dragRef.current = { ...dragRef.current, mode: 'image', pinchDist: pd, startCs: { ...csRef.current } };
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    const d = dragRef.current;
    if (!vpRef.current) return;
    const rect = vpRef.current.getBoundingClientRect();

    if (e.touches.length === 2 && d.pinchDist > 0) {
      const nd   = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
      const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
      const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
      const ss   = d.startCs;
      const newScale = Math.max(ss.fitScale, Math.min(ss.fitScale * 8, ss.imgScale * (nd / d.pinchDist)));
      const ratio    = newScale / ss.imgScale;
      applyCs(clampImg({ ...ss, imgScale: newScale, imgLeft: midX - (midX - ss.imgLeft) * ratio, imgTop: midY - (midY - ss.imgTop) * ratio }));
      return;
    }

    if (e.touches.length === 1 && d.mode !== 'none') {
      const tx = e.touches[0].clientX;
      const ty = e.touches[0].clientY;
      const dx = tx - d.startX;
      const dy = ty - d.startY;
      const ss = d.startCs;
      let next: CropState;
      if (d.mode === 'circle') {
        next = clampCrop({ ...ss, cropCX: ss.cropCX + dx, cropCY: ss.cropCY + dy });
      } else if (d.mode === 'resize') {
        const newR = Math.hypot(tx - rect.left - ss.cropCX, ty - rect.top - ss.cropCY);
        next = clampCrop({ ...ss, cropR: newR });
      } else {
        next = clampImg({ ...ss, imgLeft: ss.imgLeft + dx, imgTop: ss.imgTop + dy });
      }
      applyCs(next);
    }
  };

  const onTouchEnd = () => { dragRef.current.mode = 'none'; };

  // ── Image load → init ──────────────────────────────────────────────────────
  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img  = e.currentTarget;
    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    const w    = getVpW();

    // The circumscribed circle of the image (radius = half-diagonal) must fit inside
    // the viewport circle (radius = min(vpW, vpH) / 2 * 0.88).
    // fitScale = target_viewport_radius / natural_half_diagonal
    const targetR  = Math.min(w, VP_H) / 2 * 0.88;
    const halfDiag = Math.sqrt(natW * natW + natH * natH) / 2;
    const fitScale = targetR / halfDiag;

    const fittedW  = natW * fitScale;
    const fittedH  = natH * fitScale;
    const imgLeft  = (w    - fittedW) / 2;
    const imgTop   = (VP_H - fittedH) / 2;

    // Initial crop circle = circumscribed circle of the fitted image (covers all corners)
    const cropR = Math.sqrt((fittedW / 2) ** 2 + (fittedH / 2) ** 2);

    applyCs({ imgScale: fitScale, imgLeft, imgTop, fitScale, cropCX: w / 2, cropCY: VP_H / 2, cropR, naturalW: natW, naturalH: natH });
  };

  // ── Zoom slider ────────────────────────────────────────────────────────────
  const zoomMax     = cs.fitScale * 8;
  const zoomPct     = cs.naturalW > 0 ? Math.round(((cs.imgScale - cs.fitScale) / (zoomMax - cs.fitScale)) * 100) : 0;
  const zoomTrack   = `linear-gradient(to right,#4AB98C 0%,#4AB98C ${zoomPct}%,#e2e8f0 ${zoomPct}%,#e2e8f0 100%)`;

  const onZoomSlider = (val: number) => {
    const s    = csRef.current;
    const newScale = s.fitScale + (val / 100) * (zoomMax - s.fitScale);
    const ratio    = newScale / s.imgScale;
    applyCs(clampImg({
      ...s,
      imgScale: newScale,
      imgLeft:  s.cropCX - (s.cropCX - s.imgLeft) * ratio,
      imgTop:   s.cropCY - (s.cropCY - s.imgTop)  * ratio,
    }));
  };

  // ── File select / cancel ───────────────────────────────────────────────────
  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file type', description: 'Please select an image file.', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Please select an image smaller than 5 MB.', variant: 'destructive' });
      return;
    }
    setPendingFile(file);
    setPendingUrl(URL.createObjectURL(file));
    applyCs(DEFAULT_CS);
    setHintDismissed(false);
  };

  const cancelCrop = () => {
    if (pendingUrl) URL.revokeObjectURL(pendingUrl);
    setPendingFile(null);
    setPendingUrl('');
    applyCs(DEFAULT_CS);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Canvas crop → upload ───────────────────────────────────────────────────
  const handleCropConfirm = () => {
    if (!pendingFile || !cropImgRef.current || cs.naturalW === 0) return;
    const { imgScale, imgLeft, imgTop, cropCX, cropCY, cropR, naturalW, naturalH } = cs;
    const OUTPUT = outputSize;

    const canvas = document.createElement('canvas');
    canvas.width  = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext('2d')!;

    // Fill background black (for any areas outside the image)
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, OUTPUT, OUTPUT);

    ctx.beginPath();
    ctx.arc(OUTPUT / 2, OUTPUT / 2, OUTPUT / 2, 0, Math.PI * 2);
    ctx.clip();

    // Viewport coords → natural image coords
    const sx = (cropCX - cropR - imgLeft) / imgScale;
    const sy = (cropCY - cropR - imgTop)  / imgScale;
    const sw = (cropR * 2) / imgScale;

    // Clamp source rect to image bounds
    const csx = Math.max(0, sx);
    const csy = Math.max(0, sy);
    const csw = Math.min(sw - (csx - sx), naturalW - csx);
    const csh = Math.min(sw - (csy - sy), naturalH - csy);

    // Corresponding destination rect (maintain relative position within the OUTPUT square)
    const dx = ((csx - sx) / sw) * OUTPUT;
    const dy = ((csy - sy) / sw) * OUTPUT;
    const dw = (csw / sw) * OUTPUT;
    const dh = (csh / sw) * OUTPUT;

    if (csw > 0 && csh > 0 && dw > 0 && dh > 0) {
      ctx.drawImage(cropImgRef.current, csx, csy, csw, csh, dx, dy, dw, dh);
    }

    canvas.toBlob(
      async (blob) => {
        if (!blob) return;
        const file = new File([blob], pendingFile.name, { type: 'image/jpeg' });
        cancelCrop();
        await uploadAvatar(file);
      },
      'image/jpeg',
      0.92
    );
  };

  // ── Supabase ───────────────────────────────────────────────────────────────
  const loadCurrentAvatar = async () => {
    if (!user?.id) return;
    try {
      const { supabase } = await import('../../utils/supabaseClient');
      const { data, error } = await supabase.rpc('get_user_profile_enhanced', { user_id: user.id });
      if (!error && data && data.length > 0) {
        const path = data[0].avatar_url || '';
        if (path && !path.startsWith('http')) {
          const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);
          setAvatarUrl(publicUrl);
        } else {
          setAvatarUrl(path);
        }
      }
    } catch (e) { console.error('Error loading avatar:', e); }
  };

  const uploadAvatar = async (file: File) => {
    if (!user?.id) return;
    setUploading(true);
    try {
      const { supabase } = await import('../../utils/supabaseClient');
      const ext      = file.name.split('.').pop();
      const filePath = `${user.id}/${user.id}-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('avatars').upload(filePath, file, { cacheControl: '3600', upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(filePath);
      const { error: updateError } = await supabase.rpc('update_user_profile_enhanced', { user_id: user.id, profile_avatar: filePath });
      if (updateError) throw updateError;
      setAvatarUrl(publicUrl);
      setAvatarDims(null);
      onUploadComplete?.(publicUrl);
      toast({ title: 'Photo saved', description: 'Your profile picture has been updated.' });
    } catch (e) {
      console.error('Error uploading avatar:', e);
      toast({ title: 'Upload failed', description: 'Could not save your photo. Please try again.', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const removeAvatar = async () => {
    if (!user?.id) return;
    setUploading(true);
    try {
      const { supabase } = await import('../../utils/supabaseClient');
      if (avatarUrl) {
        const parts = avatarUrl.split('/avatars/');
        if (parts.length > 1) await supabase.storage.from('avatars').remove([parts[1]]);
      }
      await supabase.rpc('update_user_profile_enhanced', { user_id: user.id, profile_avatar: '' });
      setAvatarUrl('');
      setAvatarDims(null);
      onUploadComplete?.('');
      toast({ title: 'Photo removed' });
    } catch (e) {
      console.error('Error removing avatar:', e);
      toast({ title: 'Error', description: 'Could not remove your photo.', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const handleDragOver  = (e: React.DragEvent) => { e.preventDefault(); setDragOver(true);  };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setDragOver(false); };
  const handleDrop      = (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer.files[0]) handleFileSelect(e.dataTransfer.files[0]);
  };

  // ── Derived crop visuals ───────────────────────────────────────────────────
  const handleX = cs.cropCX + cs.cropR * Math.SQRT1_2;
  const handleY = cs.cropCY + cs.cropR * Math.SQRT1_2;

  // ── Avatar display (no crop) ───────────────────────────────────────────────
  const avatarDisplay = !pendingFile ? (
    <div className="w-full">
      <div
        className="flex flex-col items-center cursor-pointer transition-all duration-200"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="relative group mb-4">
          <div
            className={`w-32 h-32 rounded-full overflow-hidden flex items-center justify-center transition-all duration-300 ${
              dragOver
                ? 'ring-4 ring-green-400 ring-offset-2 scale-105'
                : 'ring-2 ring-gray-200 hover:ring-4 hover:ring-green-300 hover:ring-offset-2'
            }`}
            style={{ background: 'linear-gradient(135deg,#f1f5f9 0%,#e2e8f0 100%)' }}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Profile"
                className="w-full h-full object-cover"
                onLoad={e => {
                  const img = e.currentTarget;
                  setAvatarDims({ w: img.naturalWidth, h: img.naturalHeight });
                }}
              />
            ) : (
              <User className="w-14 h-14 text-gray-300" />
            )}
          </div>

          <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-all duration-200">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-center gap-1">
              <Upload className="w-6 h-6 text-white" />
              <span className="text-[11px] text-white font-medium">{avatarUrl ? 'Change' : 'Upload'}</span>
            </div>
          </div>

          {uploading && (
            <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center">
              <div className="w-7 h-7 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          )}

          {avatarUrl && !uploading && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); removeAvatar(); }}
              className="absolute -top-1 -right-1 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all duration-150 hover:scale-110"
              title="Remove photo"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <p className="text-sm font-semibold text-gray-800">
          {dragOver ? 'Drop to upload' : avatarUrl ? 'Change photo' : 'Upload photo'}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">Click or drag · PNG, JPG, GIF · max 5 MB</p>
        {avatarDims && (
          <p className="text-[11px] text-gray-400 mt-1">Saved: {avatarDims.w} × {avatarDims.h} px</p>
        )}
      </div>

      <div className="mt-4 sm:hidden">
        <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-full" size="sm">
          <Camera className="w-4 h-4 mr-2" />
          {avatarUrl ? 'Change Photo' : 'Upload Photo'}
        </Button>
      </div>
    </div>
  ) : null;

  // ── Crop section ───────────────────────────────────────────────────────────
  const cropSection = pendingFile ? (
    <div
      className="w-full rounded-2xl overflow-hidden"
      style={{ boxShadow: '0 0 0 1px rgba(0,0,0,0.08),0 8px 40px rgba(0,0,0,0.14)' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-2.5"
        style={{ background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}
      >
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#f0faf5' }}>
            <Camera className="w-3.5 h-3.5" style={{ color: '#4AB98C' }} />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-700 leading-tight">Crop Photo</p>
            <p className="text-[10px] truncate max-w-[180px] text-gray-400">
              {cs.naturalW > 0 ? `${pendingFile.name}` : 'Loading…'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={cancelCrop}
          className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-gray-100"
          style={{ background: '#f3f4f6' }}
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      {/* ── Crop viewport */}
      <div
        ref={vpRef}
        className="relative select-none overflow-hidden"
        style={{
          height: VP_H,
          background: '#1a1f2e',
          cursor: vpCursor,
          touchAction: 'none',
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMoveVp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Checkerboard behind image (shows transparency) */}
        {cs.naturalW > 0 && (
          <div
            style={{
              position: 'absolute',
              left: cs.imgLeft,
              top: cs.imgTop,
              width: cs.naturalW * cs.imgScale,
              height: cs.naturalH * cs.imgScale,
              backgroundImage: 'repeating-conic-gradient(#2a2f3e 0% 25%, #222736 0% 50%)',
              backgroundSize: '16px 16px',
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Source image */}
        <img
          ref={cropImgRef}
          src={pendingUrl}
          alt=""
          style={{
            position: 'absolute',
            left:   cs.imgLeft,
            top:    cs.imgTop,
            width:  cs.naturalW > 0 ? cs.naturalW * cs.imgScale : 'auto',
            height: cs.naturalH > 0 ? cs.naturalH * cs.imgScale : 'auto',
            maxWidth: 'none',
            maxHeight: 'none',
            pointerEvents: 'none',
            userSelect: 'none',
            willChange: 'transform',
            transformOrigin: 'top left',
          }}
          draggable={false}
          onLoad={onImageLoad}
        />

        {/* Dark overlay with circular cutout */}
        {cs.naturalW > 0 && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(circle ${cs.cropR}px at ${cs.cropCX}px ${cs.cropCY}px, transparent ${cs.cropR - 1}px, rgba(0,0,0,0.62) ${cs.cropR}px)`,
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Circle border */}
        {cs.naturalW > 0 && (
          <div
            style={{
              position: 'absolute',
              left:   cs.cropCX - cs.cropR,
              top:    cs.cropCY - cs.cropR,
              width:  cs.cropR * 2,
              height: cs.cropR * 2,
              borderRadius: '50%',
              border: '2.5px solid #4AB98C',
              boxShadow: '0 0 0 3px rgba(74,185,140,0.15)',
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Rule-of-thirds grid (clipped inside circle) */}
        {cs.naturalW > 0 && (
          <div
            style={{
              position: 'absolute',
              left: cs.cropCX - cs.cropR,
              top:  cs.cropCY - cs.cropR,
              width:  cs.cropR * 2,
              height: cs.cropR * 2,
              borderRadius: '50%',
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            {['33.33%', '66.66%'].map(pos => (
              <React.Fragment key={pos}>
                <div style={{ position: 'absolute', left: 0, right: 0, top: pos, height: 1, background: 'rgba(74,185,140,0.3)' }} />
                <div style={{ position: 'absolute', top: 0, bottom: 0, left: pos, width: 1, background: 'rgba(74,185,140,0.3)' }} />
              </React.Fragment>
            ))}
          </div>
        )}

        {/* SE resize handle */}
        {cs.naturalW > 0 && (
          <div
            style={{
              position: 'absolute',
              left:   handleX - 8,
              top:    handleY - 8,
              width:  16,
              height: 16,
              borderRadius: '50%',
              background: 'white',
              border: '2.5px solid #4AB98C',
              boxShadow: '0 1px 6px rgba(0,0,0,0.6)',
              pointerEvents: 'none',
              cursor: 'nwse-resize',
            }}
          />
        )}

        {/* Loading spinner */}
        {cs.naturalW === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full border-2 border-white/20 border-t-green-400 animate-spin" />
          </div>
        )}

        {/* Hint — dismisses on first interaction */}
        {cs.naturalW > 0 && !hintDismissed && (
          <div className="absolute bottom-3 left-0 right-0 flex justify-center pointer-events-none">
            <span
              className="text-[11px] rounded-full px-3 py-1"
              style={{ color: '#6b7280', background: 'rgba(255,255,255,0.85)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
            >
              Drag image to reposition · Drag handle to resize · Scroll to zoom
            </span>
          </div>
        )}
      </div>

      {/* Controls panel — compact single row */}
      <div className="bg-white px-4 py-3 flex items-center gap-2 border-t border-gray-100">
        {/* Zoom */}
        <ZoomIn className="w-3.5 h-3.5 text-gray-400 shrink-0" />
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={zoomPct}
          onChange={e => onZoomSlider(parseInt(e.target.value))}
          disabled={cs.naturalW === 0}
          className="h-1 rounded-full appearance-none cursor-pointer"
          style={{ flex: '1 1 0', minWidth: 0, background: zoomTrack, accentColor: '#4AB98C' }}
        />
        <span className="text-[10px] font-mono font-bold shrink-0" style={{ color: '#4AB98C', minWidth: 32, textAlign: 'right' }}>
          {cs.naturalW > 0 ? `${Math.round((cs.imgScale / cs.fitScale) * 100)}%` : '—'}
        </span>

        <div className="w-px h-4 bg-gray-200 shrink-0 mx-1" />

        {/* Buttons */}
        <button
          type="button"
          onClick={cancelCrop}
          disabled={uploading}
          className="text-xs text-gray-500 hover:text-gray-700 font-medium shrink-0 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleCropConfirm}
          disabled={uploading || cs.naturalW === 0}
          className="flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-1.5 rounded-lg shrink-0 transition-opacity disabled:opacity-50 whitespace-nowrap"
          style={{ background: 'linear-gradient(135deg,#4AB98C 0%,#2d9b6f 100%)', boxShadow: '0 2px 8px rgba(74,185,140,0.35)' }}
        >
          {uploading
            ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <Check className="h-3 w-3 shrink-0" />
          }
          {uploading ? 'Saving…' : 'Save Photo'}
        </button>
      </div>
    </div>
  ) : null;

  // ── Render ─────────────────────────────────────────────────────────────────
  const content = (
    <div className="flex flex-col items-center justify-center w-full space-y-4">
      {avatarDisplay}
      {cropSection}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={e => { if (e.target.files?.[0]) handleFileSelect(e.target.files[0]); }}
        className="hidden"
        disabled={uploading}
      />
    </div>
  );

  if (showTitle) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Profile Picture
          </CardTitle>
          <CardDescription>Upload, crop and save your profile picture</CardDescription>
        </CardHeader>
        <CardContent>{content}</CardContent>
      </Card>
    );
  }

  return content;
};
